import { Text, modals } from "@dehub/theme";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useBlocker } from "react-router-dom";

/**
 * Warns before unsaved work is lost: closing or reloading the tab (beforeunload) and every in-app
 * navigation, browser back included (react-router useBlocker, needs a data router). Navigation to the
 * same URL is not blocked.
 */
export function useUnsavedGuard(dirty: boolean) {
  const { t } = useTranslation("common");

  useEffect(() => {
    if (!dirty) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirty && (currentLocation.pathname !== nextLocation.pathname || currentLocation.search !== nextLocation.search),
  );

  useEffect(() => {
    if (blocker.state !== "blocked") return;
    modals.openConfirmModal({
      title: t("unsaved.title"),
      children: <Text fz={15}>{t("unsaved.text")}</Text>,
      labels: { confirm: t("unsaved.leave"), cancel: t("unsaved.stay") },
      confirmProps: { color: "red" },
      centered: true,
      onConfirm: () => blocker.proceed(),
      onCancel: () => blocker.reset(),
      onClose: () => blocker.reset(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocker.state]);
}
